import React, {
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import * as yup from 'yup';
import { formatError, prepareErrorsFromYupValidationError } from 'src/utils/helpers/errors';
import { AppContext } from 'src/context';
import omit from 'lodash/omit';
import { useGetLocations } from 'src/hooks/queries/locations/useGetLocations';
import get from 'lodash/get';
import { useCreateNotice } from 'src/hooks/queries/noticeboard/useCreateNotice';
import { useEditNotice } from 'src/hooks/queries/noticeboard/useEditNotice';
import type { AxiosError } from 'axios';
import isEqual from 'lodash/isEqual';
import type { TCreateEditNoticeProps } from '../types';

export const initFormErrors: Partial<Record<keyof TCreateEditNoticeProps, string[]>> = {
    title: [],
    text: [],
};

export const useCreateEditNoticeModalState = (props: TCreateEditNoticeProps) => {
    const { isEdit = false } = props;
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    const {
        actions: { handleCloseModal },
    } = useContext(AppContext);
    const [state, setState] = useState<TCreateEditNoticeProps>({
        title: '',
        text: '',
        location_id: props.location_id ? props.location_id : '',
    });
    const [errors, setErrors] =
        useState<Partial<Record<keyof TCreateEditNoticeProps, string[]>>>(initFormErrors);

    const { mutateAsync: createNotice, isLoading: isCreatingNotice } = useCreateNotice();
    const { mutateAsync: editNotice, isLoading: isEditingNotice } = useEditNotice(
        props.id as string
    );

    // Getting locations list
    const { data, isLoading } = useGetLocations();
    const locations = useMemo(
        () => [
            { label: 'All locations', value: '' },
            ...get(data, 'data', []).map((location) => ({
                label: location.name,
                value: location.id,
            })),
        ],
        [data]
    );

    useEffect(() => {
        if (
            isEdit &&
            (isEqual(state, { text: '', title: '', location_id: '' }) ||
                isEqual(state, { text: '', title: '', location_id: props.location_id }))
        ) {
            setState((prevState) => ({ ...prevState, ...omit(props, ['isEdit', 'id']) }));
        }
    }, [isEdit, props, state, state.title]);

    const validationSchema = yup.object().shape({
        text: yup
            .string()
            .required('Message is required')
            .typeError('Message is required')
            .min(2, 'Too short message')
            .max(256, 'Too long message'),
        title: yup
            .string()
            .required('Notice Subject is required')
            .typeError('Notice Subject is required')
            .min(2, 'Too short Subject')
            .max(256, 'Too long Subject'),
    });

    const handleValidate = useCallback(async () => {
        try {
            await validationSchema.validate(state, { abortEarly: false, recursive: true });
            return true;
        } catch (err) {
            const result = prepareErrorsFromYupValidationError<TCreateEditNoticeProps>(
                err as unknown as yup.ValidationError
            );
            setErrors(result);
            return false;
        }
    }, [state, validationSchema]);

    const handleHtmlEvent = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            const { name } = event.currentTarget;
            const value: unknown = event.currentTarget?.value;

            setState({
                ...state,
                [name]: value,
            });
        },
        [state]
    );

    const handleSelect = useCallback(
        (value: string) => {
            setState({
                ...state,
                location_id: value === '' ? null : value,
            });
        },
        [state]
    );

    const handleSubmit = useCallback(
        async (event: SyntheticEvent) => {
            event.preventDefault();

            const isValid = await handleValidate();

            if (!isValid) {
                return;
            }

            try {
                if (!isEdit) {
                    await createNotice(state);
                } else {
                    await editNotice(omit(state, ['locationId', 'created_at']));
                }
                handleCloseModal();
            } catch (error) {
                setServerErrors(formatError(error as AxiosError<{ message: string }>));
            }
        },
        [state, isEdit, createNotice, editNotice, handleCloseModal, handleValidate]
    );

    return {
        state,
        handleHtmlEvent,
        handleSelect,
        errors,
        handleSubmit,
        handleCloseModal,
        locations,
        isLoading: isLoading || isCreatingNotice || isEditingNotice,
        serverErrors,
        setServerErrors,
    };
};
